import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertCircle, Printer, ArrowLeft, CheckCircle, RefreshCw, XCircle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { logActivity } from '@/lib/activity-log';
import type { ProductInsert } from '@/integrations/supabase/types';

interface StockComparison {
  productCode: string;
  productName: string;
  category: string;
  currentQuantity: number;
  uploadedQuantity: number;
  quantityChange: number;
  currentCost: number | null;
  uploadedCost: number | null;
  productId?: string;
  isNew: boolean;
  willBeRemoved?: boolean;
}

interface ComparisonData {
  comparison: StockComparison[];
  excelItems: any[];
  mode: 'update' | 'clean_slate';
}

const AnnualInventoryComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, user, refreshSession, isSessionValid } = useAuth();
  const { currentLocation } = useLocationContext();

  const comparisonData = location.state as ComparisonData | null;
  const [comparison, setComparison] = useState<StockComparison[]>(comparisonData?.comparison || []);
  const [excelItems, setExcelItems] = useState<any[]>(comparisonData?.excelItems || []);
  const [mode, setMode] = useState<'update' | 'clean_slate'>(comparisonData?.mode || 'update');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!comparisonData || !comparison.length) {
      toast.error('Karşılaştırma verisi bulunamadı');
      navigate('/stock-entry');
    }
  }, [comparisonData, comparison.length, navigate]);

  const printComparison = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const modeText = mode === 'update' ? 'Güncelleme Modu' : 'Tam Değiştirme Modu';
    const date = new Date().toLocaleString('tr-TR');
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Yıllık Sayım Karşılaştırma Raporu</title>
        <meta charset="UTF-8">
        <style>
          @media print {
            @page { margin: 1cm; }
            body { margin: 0; }
          }
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
          h1 { color: #333; border-bottom: 3px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          h2 { color: #666; margin-top: 30px; margin-bottom: 15px; border-bottom: 2px solid #666; padding-bottom: 5px; }
          h3 { color: #888; margin-top: 20px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 20px; page-break-inside: auto; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
          th { background-color: #f2f2f2; font-weight: bold; position: sticky; top: 0; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          .new { background-color: #d4edda; }
          .updated { background-color: #fff3cd; }
          .removed { background-color: #f8d7da; }
          .increase { color: green; font-weight: bold; }
          .decrease { color: red; font-weight: bold; }
          .summary { margin: 20px 0; padding: 15px; background-color: #e7f3ff; border-left: 4px solid #2196F3; }
          .warning { background-color: #fee2e2; border-left: 4px solid #dc2626; padding: 15px; margin: 20px 0; }
          .section-summary { margin: 10px 0; padding: 10px; background-color: #f0f0f0; border-radius: 4px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-bold { font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>Yıllık Sayım Detaylı Karşılaştırma Raporu</h1>
        <div class="summary">
          <p><strong>Mod:</strong> ${modeText}</p>
          <p><strong>Oluşturma Tarihi:</strong> ${date}</p>
          <p><strong>Toplam Ürün Sayısı:</strong> ${comparison.length}</p>
          <p><strong>Yeni Ürün:</strong> ${comparison.filter(c => c.isNew).length}</p>
          <p><strong>Güncellenecek Ürün:</strong> ${comparison.filter(c => !c.isNew && !c.willBeRemoved).length}</p>
          ${mode === 'clean_slate' ? `<p><strong>Sıfırlanacak Ürün:</strong> ${comparison.filter(c => c.willBeRemoved).length}</p>` : ''}
        </div>
    `;

    // Calculate totals
    const totalCurrentQuantity = comparison.reduce((sum, c) => sum + c.currentQuantity, 0);
    const totalUploadedQuantity = comparison.reduce((sum, c) => sum + c.uploadedQuantity, 0);
    const totalQuantityChange = comparison.reduce((sum, c) => sum + c.quantityChange, 0);
    const totalCurrentValue = comparison.reduce((sum, c) => sum + ((c.currentCost || 0) * c.currentQuantity), 0);
    const totalUploadedValue = comparison.reduce((sum, c) => sum + ((c.uploadedCost || 0) * c.uploadedQuantity), 0);
    const totalValueChange = totalUploadedValue - totalCurrentValue;

    html += `
      <div class="section-summary">
        <h3>Toplam Değerler</h3>
        <table>
          <tr>
            <th>Özellik</th>
            <th class="text-right">Mevcut Stok</th>
            <th class="text-right">Excel'deki Stok</th>
            <th class="text-right">Fark</th>
          </tr>
          <tr>
            <td><strong>Toplam Miktar</strong></td>
            <td class="text-right">${totalCurrentQuantity.toLocaleString('tr-TR')} adet</td>
            <td class="text-right">${totalUploadedQuantity.toLocaleString('tr-TR')} adet</td>
            <td class="text-right ${totalQuantityChange >= 0 ? 'increase' : 'decrease'}">${totalQuantityChange >= 0 ? '+' : ''}${totalQuantityChange.toLocaleString('tr-TR')} adet</td>
          </tr>
          <tr>
            <td><strong>Toplam Değer</strong></td>
            <td class="text-right">₺${totalCurrentValue.toFixed(2)}</td>
            <td class="text-right">₺${totalUploadedValue.toFixed(2)}</td>
            <td class="text-right ${totalValueChange >= 0 ? 'increase' : 'decrease'}">${totalValueChange >= 0 ? '+' : ''}₺${totalValueChange.toFixed(2)}</td>
          </tr>
        </table>
      </div>
    `;

    // Major discrepancies (decreases in quantity)
    const majorDiscrepancies = comparison.filter(c => 
      !c.isNew && 
      !c.willBeRemoved && 
      c.quantityChange < 0 && 
      c.currentQuantity > 0
    );

    if (majorDiscrepancies.length > 0) {
      html += `<h2 style="color: #dc2626;">⚠️ ÖNEMLİ UYARI: Stok Farkları Tespit Edildi (${majorDiscrepancies.length})</h2>`;
      html += `<div class="warning">
        <p><strong>Aşağıdaki ${majorDiscrepancies.length} üründe mevcut stoktan daha az miktar tespit edildi.</strong></p>
        <p>Bu durum stok kaybı veya sayım hatası gösterebilir. Lütfen kontrol ediniz.</p>
      </div>`;
      html += `<table style="margin-top: 10px;">
        <tr>
          <th>Ürün Kodu</th>
          <th>Ürün Adı</th>
          <th>Kategori</th>
          <th class="text-right">Mevcut Stok</th>
          <th class="text-right">Excel'deki Miktar</th>
          <th class="text-right">Fark (Eksik)</th>
          <th class="text-right">Mevcut Maliyet</th>
          <th class="text-right">Excel Maliyeti</th>
          <th class="text-right">Mevcut Değer</th>
          <th class="text-right">Yeni Değer</th>
        </tr>`;
      majorDiscrepancies.forEach(c => {
        const currentValue = (c.currentCost || 0) * c.currentQuantity;
        const uploadedValue = (c.uploadedCost || 0) * c.uploadedQuantity;
        html += `<tr style="background-color: #fee2e2;">
          <td><strong>${c.productCode}</strong></td>
          <td>${c.productName}</td>
          <td>${c.category || '-'}</td>
          <td class="text-right" style="color: #dc2626; font-weight: bold;">${c.currentQuantity.toLocaleString('tr-TR')} adet</td>
          <td class="text-right" style="color: #dc2626; font-weight: bold;">${c.uploadedQuantity.toLocaleString('tr-TR')} adet</td>
          <td class="text-right" style="color: #dc2626; font-weight: bold;">${c.quantityChange.toLocaleString('tr-TR')} adet</td>
          <td class="text-right">${c.currentCost ? '₺' + c.currentCost.toFixed(2) : '-'}</td>
          <td class="text-right">${c.uploadedCost ? '₺' + c.uploadedCost.toFixed(2) : '-'}</td>
          <td class="text-right">₺${currentValue.toFixed(2)}</td>
          <td class="text-right">₺${uploadedValue.toFixed(2)}</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // New products
    const newProducts = comparison.filter(c => c.isNew);
    if (newProducts.length > 0) {
      html += `<h2>✅ Yeni Ürünler (${newProducts.length})</h2>`;
      html += `<table>
        <tr>
          <th>Ürün Kodu</th>
          <th>Ürün Adı</th>
          <th>Kategori</th>
          <th class="text-right">Miktar</th>
          <th class="text-right">Maliyet</th>
          <th class="text-right">Toplam Değer</th>
        </tr>`;
      newProducts.forEach(c => {
        const totalValue = (c.uploadedCost || 0) * c.uploadedQuantity;
        html += `<tr class="new">
          <td>${c.productCode}</td>
          <td>${c.productName}</td>
          <td>${c.category || '-'}</td>
          <td class="text-right">${c.uploadedQuantity.toLocaleString('tr-TR')}</td>
          <td class="text-right">${c.uploadedCost ? '₺' + c.uploadedCost.toFixed(2) : '-'}</td>
          <td class="text-right">₺${totalValue.toFixed(2)}</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // Updated products
    const updatedProducts = comparison.filter(c => !c.isNew && !c.willBeRemoved);
    if (updatedProducts.length > 0) {
      html += `<h2>🔄 Güncellenecek Ürünler (${updatedProducts.length})</h2>`;
      html += `<table>
        <tr>
          <th>Ürün Kodu</th>
          <th>Ürün Adı</th>
          <th>Kategori</th>
          <th class="text-right">Mevcut Miktar</th>
          <th class="text-right">Yeni Miktar</th>
          <th class="text-right">Değişim</th>
          <th class="text-right">Mevcut Maliyet</th>
          <th class="text-right">Yeni Maliyet</th>
          <th class="text-right">Mevcut Değer</th>
          <th class="text-right">Yeni Değer</th>
          <th class="text-right">Değer Farkı</th>
        </tr>`;
      updatedProducts.forEach(c => {
        const changeClass = c.quantityChange > 0 ? 'increase' : c.quantityChange < 0 ? 'decrease' : '';
        const changeText = c.quantityChange > 0 ? `+${c.quantityChange.toLocaleString('tr-TR')}` : c.quantityChange.toLocaleString('tr-TR');
        const currentValue = (c.currentCost || 0) * c.currentQuantity;
        const uploadedValue = (c.uploadedCost || 0) * c.uploadedQuantity;
        const valueChange = uploadedValue - currentValue;
        const valueChangeClass = valueChange >= 0 ? 'increase' : 'decrease';
        html += `<tr class="updated">
          <td>${c.productCode}</td>
          <td>${c.productName}</td>
          <td>${c.category || '-'}</td>
          <td class="text-right">${c.currentQuantity.toLocaleString('tr-TR')}</td>
          <td class="text-right">${c.uploadedQuantity.toLocaleString('tr-TR')}</td>
          <td class="text-right ${changeClass}">${changeText}</td>
          <td class="text-right">${c.currentCost ? '₺' + c.currentCost.toFixed(2) : '-'}</td>
          <td class="text-right">${c.uploadedCost ? '₺' + c.uploadedCost.toFixed(2) : '-'}</td>
          <td class="text-right">₺${currentValue.toFixed(2)}</td>
          <td class="text-right">₺${uploadedValue.toFixed(2)}</td>
          <td class="text-right ${valueChangeClass}">${valueChange >= 0 ? '+' : ''}₺${valueChange.toFixed(2)}</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // Removed products (full wipe mode only)
    if (mode === 'clean_slate') {
      const removedProducts = comparison.filter(c => c.willBeRemoved);
      if (removedProducts.length > 0) {
        html += `<h2>❌ Sıfırlanacak Ürünler (${removedProducts.length})</h2>`;
        html += `<table>
          <tr>
            <th>Ürün Kodu</th>
            <th>Ürün Adı</th>
            <th>Kategori</th>
            <th class="text-right">Mevcut Miktar</th>
            <th class="text-right">Mevcut Maliyet</th>
            <th class="text-right">Mevcut Değer</th>
          </tr>`;
        removedProducts.forEach(c => {
          const currentValue = (c.currentCost || 0) * c.currentQuantity;
          html += `<tr class="removed">
            <td>${c.productCode}</td>
            <td>${c.productName}</td>
            <td>${c.category || '-'}</td>
            <td class="text-right">${c.currentQuantity.toLocaleString('tr-TR')}</td>
            <td class="text-right">${c.currentCost ? '₺' + c.currentCost.toFixed(2) : '-'}</td>
            <td class="text-right">₺${currentValue.toFixed(2)}</td>
          </tr>`;
        });
        html += `</table>`;
      }
    }

    html += `
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #333;">
        <p style="text-align: center; color: #666; font-size: 10px;">
          Bu rapor ${date} tarihinde oluşturulmuştur.
        </p>
      </div>
    </body></html>`;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const exportToCSV = () => {
    const headers = [
      'Ürün Kodu',
      'Ürün Adı',
      'Kategori',
      'Durum',
      'Mevcut Miktar',
      'Excel Miktarı',
      'Miktar Değişimi',
      'Mevcut Maliyet',
      'Excel Maliyeti',
      'Mevcut Değer',
      'Yeni Değer',
      'Değer Farkı'
    ];

    const rows = comparison.map(c => {
      const status = c.isNew ? 'Yeni' : c.willBeRemoved ? 'Sıfırlanacak' : 'Güncellenecek';
      const currentValue = (c.currentCost || 0) * c.currentQuantity;
      const uploadedValue = (c.uploadedCost || 0) * c.uploadedQuantity;
      const valueChange = uploadedValue - currentValue;

      return [
        c.productCode,
        c.productName,
        c.category || '',
        status,
        c.currentQuantity.toString(),
        c.uploadedQuantity.toString(),
        c.quantityChange.toString(),
        c.currentCost ? c.currentCost.toFixed(2) : '',
        c.uploadedCost ? c.uploadedCost.toFixed(2) : '',
        currentValue.toFixed(2),
        uploadedValue.toFixed(2),
        valueChange.toFixed(2)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `yillik-sayim-karsilastirma-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApprove = async () => {
    if (!companyId || !user) {
      toast.error('Yetkilendirme hatası. Lütfen tekrar giriş yapın.');
      return;
    }

    // Confirm action
    if (!window.confirm('Stok miktarlarını Excel\'deki değerlere göre güncellemek istediğinize emin misiniz? Bu işlem geri alınamaz. Excel\'de olmayan ürünlerin stokları sıfırlanacak.')) {
      return;
    }

    setSaving(true);
    const currentDate = new Date().toISOString();
    const results = {
      created: 0,
      updated: 0,
      zeroed: 0,
      errors: 0
    };

    try {
      // Check and refresh session if needed
      if (!isSessionValid()) {
        toast.info('Oturum yenileniyor...');
        await refreshSession();
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Fetch all existing products for this company
      const { data: allProducts, error: fetchError } = await supabase
        .from('products')
        .select('id, item_code, name, category, quantity_on_hand, current_cost')
        .eq('company_id', companyId);

      if (fetchError) {
        throw fetchError;
      }

      // Create a map of existing products by product code (lowercase for case-insensitive matching)
      const existingProductsMap = new Map<string, typeof allProducts[0]>();
      allProducts?.forEach(product => {
        if (product.item_code) {
          existingProductsMap.set(product.item_code.toLowerCase(), product);
        }
      });

      // Track which product codes from Excel we've processed
      const processedProductCodes = new Set<string>();

      // Process ALL items from Excel (after user merge/create choices)
      for (const excelItem of excelItems) {
        try {
          if (!excelItem.productCode || excelItem.productCode.trim() === '') {
            console.warn('Skipping item without product code:', excelItem);
            continue;
          }

          const productCode = excelItem.productCode.trim();
          const productCodeLower = productCode.toLowerCase();
          processedProductCodes.add(productCodeLower);

          const excelQuantity = parseInt(excelItem.quantity) || 0;
          const excelCost = parseFloat(excelItem.cost) || 0;
          const excelName = excelItem.productName?.trim() || '';
          const excelCategory = excelItem.category?.trim() || '';

          const existingProduct = existingProductsMap.get(productCodeLower);

          if (existingProduct) {
            // Update existing product - SET quantity to Excel quantity (wipe old quantity)
            const previousQuantity = existingProduct.quantity_on_hand || 0;
            const quantityChange = excelQuantity - previousQuantity;
            
            const updateData: any = {
              quantity_on_hand: excelQuantity,
            };

            // Update cost if uploaded cost is higher, or if provided for corrections
            const existingCost = existingProduct.current_cost || 0;
            if (excelCost > existingCost) {
              updateData.current_cost = excelCost;
              updateData.last_price_update = currentDate;
            } else if (excelCost > 0 && excelCost !== existingCost) {
              // Update cost even if not higher (for corrections)
              updateData.current_cost = excelCost;
            }

            // Update name and category if provided
            if (excelName && excelName !== existingProduct.name) {
              updateData.name = excelName;
            }
            if (excelCategory && excelCategory !== existingProduct.category) {
              updateData.category = excelCategory;
            }

            const { error: updateError } = await supabase
              .from('products')
              .update(updateData)
              .eq('id', existingProduct.id);

            if (updateError) {
              throw updateError;
            }

            // Create inventory movement if quantity changed
            if (quantityChange !== 0) {
              const movementType = quantityChange > 0 ? 'purchase_in' : 'adjustment_out';
              const movementQty = Math.abs(quantityChange);

              await supabase
                .from('inventory_movements')
                .insert({
                  company_id: companyId,
                  product_id: existingProduct.id,
                  movement_type: movementType,
                  qty: movementQty,
                  related_table: 'annual_inventory',
                  related_id: null,
                  actor_user_id: user.id,
                });
            }

            // Log activity
            await logActivity({
              action: 'update',
              entity: 'product',
              entityId: existingProduct.id,
              detail: {
                name: excelName || existingProduct.name,
                item_code: productCode,
                changes: updateData,
                quantity_change: quantityChange,
                previous_quantity: previousQuantity,
                new_quantity: excelQuantity,
                source: 'annual_inventory'
              },
              locationId: currentLocation?.id || null,
              companyId,
              userId: user.id,
            });

            results.updated++;
          } else {
            // Create new product
            const productData: ProductInsert = {
              company_id: companyId,
              item_code: productCode,
              name: excelName || 'Yeni Ürün',
              category: excelCategory || '',
              initial_cost: excelCost,
              current_cost: excelCost,
              price: 0,
              quantity_on_hand: excelQuantity,
              entry_date: currentDate,
              meta: {
                createdVia: 'annual_inventory',
                brand: ''
              }
            };

            const { data: newProduct, error: insertError } = await supabase
              .from('products')
              .insert(productData)
              .select('id')
              .single();

            if (insertError) {
              throw insertError;
            }

            // Create initial inventory movement if quantity is not 0
            if (excelQuantity !== 0) {
              await supabase
                .from('inventory_movements')
                .insert({
                  company_id: companyId,
                  product_id: newProduct.id,
                  movement_type: 'initial',
                  qty: Math.abs(excelQuantity),
                  related_table: 'annual_inventory',
                  related_id: null,
                  actor_user_id: user.id,
                });
            }

            // Log activity
            await logActivity({
              action: 'create',
              entity: 'product',
              entityId: newProduct.id,
              detail: {
                name: excelName,
                item_code: productCode,
                category: excelCategory,
                quantity: excelQuantity,
                cost: excelCost,
                source: 'annual_inventory'
              },
              locationId: currentLocation?.id || null,
              companyId,
              userId: user.id,
            });

            results.created++;
          }
        } catch (itemError) {
          console.error(`Error processing Excel item ${excelItem.productCode}:`, itemError);
          results.errors++;
        }
      }

      // Set all products NOT in Excel to quantity 0 (wipe old quantities)
      for (const [productCodeLower, product] of existingProductsMap.entries()) {
        if (!processedProductCodes.has(productCodeLower)) {
          try {
            const previousQuantity = product.quantity_on_hand || 0;
            
            // Only update if quantity is not already 0
            if (previousQuantity !== 0) {
              const { error: zeroError } = await supabase
                .from('products')
                .update({ quantity_on_hand: 0 })
                .eq('id', product.id);

              if (zeroError) {
                throw zeroError;
              }

              // Create inventory movement for zeroing out
              await supabase
                .from('inventory_movements')
                .insert({
                  company_id: companyId,
                  product_id: product.id,
                  movement_type: 'adjustment_out',
                  qty: previousQuantity,
                  related_table: 'annual_inventory',
                  related_id: null,
                  actor_user_id: user.id,
                });

              // Log activity
              await logActivity({
                action: 'update',
                entity: 'product',
                entityId: product.id,
                detail: {
                  name: product.name,
                  item_code: product.item_code,
                  quantity_change: -previousQuantity,
                  previous_quantity: previousQuantity,
                  new_quantity: 0,
                  source: 'annual_inventory_zeroed'
                },
                locationId: currentLocation?.id || null,
                companyId,
                userId: user.id,
              });

              results.zeroed++;
            }
          } catch (zeroError) {
            console.error(`Error zeroing product ${product.item_code}:`, zeroError);
            results.errors++;
          }
        }
      }

      // Show success message
      const messages = [];
      if (results.created > 0) messages.push(`${results.created} yeni ürün oluşturuldu`);
      if (results.updated > 0) messages.push(`${results.updated} ürün güncellendi`);
      if (results.zeroed > 0) messages.push(`${results.zeroed} ürün stoku sıfırlandı`);
      if (results.errors > 0) messages.push(`${results.errors} hatalı ürün işlenemedi`);

      if (results.created > 0 || results.updated > 0 || results.zeroed > 0) {
        toast.success('Yıllık sayım stok güncellemesi tamamlandı! ' + messages.join(', '));
        // Navigate back to stock entry
        setTimeout(() => {
          navigate('/stock-entry');
        }, 1500);
      } else {
        toast.error('Hiçbir ürün güncellenemedi');
      }
    } catch (error) {
      console.error('Error saving annual inventory:', error);
      toast.error('Stok güncellemesi sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = () => {
    if (window.confirm('Yıllık sayım işlemini iptal etmek istediğinize emin misiniz? Tüm değişiklikler kaybolacak.')) {
      navigate('/stock-entry');
    }
  };

  if (!comparison.length) {
    return null;
  }

  const majorDiscrepancies = comparison.filter(c => 
    !c.isNew && 
    !c.willBeRemoved && 
    c.quantityChange < 0 && 
    c.currentQuantity > 0
  );

  const newProducts = comparison.filter(c => c.isNew);
  const updatedProducts = comparison.filter(c => !c.isNew && !c.willBeRemoved);
  const removedProducts = comparison.filter(c => c.willBeRemoved);

  const totalCurrentQuantity = comparison.reduce((sum, c) => sum + c.currentQuantity, 0);
  const totalUploadedQuantity = comparison.reduce((sum, c) => sum + c.uploadedQuantity, 0);
  const totalQuantityChange = comparison.reduce((sum, c) => sum + c.quantityChange, 0);
  const totalCurrentValue = comparison.reduce((sum, c) => sum + ((c.currentCost || 0) * c.currentQuantity), 0);
  const totalUploadedValue = comparison.reduce((sum, c) => sum + ((c.uploadedCost || 0) * c.uploadedQuantity), 0);
  const totalValueChange = totalUploadedValue - totalCurrentValue;

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/stock-entry')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri
            </Button>
            <div>
              <h1 className="text-h1">Yıllık Sayım Detaylı Karşılaştırma</h1>
              <p className="text-muted-foreground">
                Excel'deki stok ile mevcut stok karşılaştırması
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              className="flex items-center gap-2"
              disabled={saving}
            >
              <Download className="w-4 h-4" />
              CSV İndir
            </Button>
            <Button
              variant="outline"
              onClick={printComparison}
              className="flex items-center gap-2"
              disabled={saving}
            >
              <Printer className="w-4 h-4" />
              Yazdır
            </Button>
            <Button
              variant="outline"
              onClick={handleReject}
              className="flex items-center gap-2 text-destructive hover:bg-destructive/10"
              disabled={saving}
            >
              <XCircle className="w-4 h-4" />
              İptal Et
            </Button>
            <Button
              onClick={handleApprove}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Onayla ve Kaydet
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-500">{comparison.length}</div>
                <div className="text-sm text-muted-foreground">Toplam Ürün</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">{newProducts.length}</div>
                <div className="text-sm text-muted-foreground">Yeni Ürün</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-500">{updatedProducts.length}</div>
                <div className="text-sm text-muted-foreground">Güncellenecek</div>
              </div>
            </CardContent>
          </Card>
          {mode === 'clean_slate' && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-500">{removedProducts.length}</div>
                  <div className="text-sm text-muted-foreground">Sıfırlanacak</div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Total Values */}
        <Card>
          <CardHeader>
            <CardTitle>Toplam Değerler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Mevcut Toplam Miktar</Label>
                <p className="text-xl font-bold">{totalCurrentQuantity.toLocaleString('tr-TR')} adet</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Excel Toplam Miktar</Label>
                <p className="text-xl font-bold">{totalUploadedQuantity.toLocaleString('tr-TR')} adet</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Mevcut Toplam Değer</Label>
                <p className="text-xl font-bold">₺{totalCurrentValue.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Yeni Toplam Değer</Label>
                <p className={`text-xl font-bold ${totalValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₺{totalUploadedValue.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Net Değişim:</span>
                <span className={`text-2xl font-bold ${totalQuantityChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalQuantityChange >= 0 ? '+' : ''}{totalQuantityChange.toLocaleString('tr-TR')} adet
                </span>
                <span className={`text-2xl font-bold ${totalValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalValueChange >= 0 ? '+' : ''}₺{totalValueChange.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Major Discrepancies Warning */}
        {majorDiscrepancies.length > 0 && (
          <Card className="border-red-500 bg-red-500/10">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Önemli Uyarı: Stok Farkları Tespit Edildi ({majorDiscrepancies.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Aşağıdaki <strong>{majorDiscrepancies.length} üründe</strong> mevcut stoktan daha az miktar tespit edildi. 
                Bu durum stok kaybı veya sayım hatası gösterebilir. Lütfen kontrol ediniz.
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {majorDiscrepancies.map((comp, idx) => {
                  const currentValue = (comp.currentCost || 0) * comp.currentQuantity;
                  const uploadedValue = (comp.uploadedCost || 0) * comp.uploadedQuantity;
                  return (
                    <div key={idx} className="p-3 rounded-lg border-2 border-red-500/30 bg-red-500/5">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                          <div className="font-bold">{comp.productCode}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                          <div className="font-medium">{comp.productName}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Stok:</span>
                          <div className="font-bold text-red-600">{comp.currentQuantity.toLocaleString('tr-TR')} adet</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Excel'deki Miktar:</span>
                          <div className="font-bold text-red-600">{comp.uploadedQuantity.toLocaleString('tr-TR')} adet</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Fark:</span>
                          <div className="font-bold text-red-600">{comp.quantityChange.toLocaleString('tr-TR')} adet</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-red-500/20 grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Değer:</span>
                          <div className="font-medium">₺{currentValue.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Yeni Değer:</span>
                          <div className="font-medium">₺{uploadedValue.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* New Products */}
        {newProducts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-green-600 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Yeni Ürünler ({newProducts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {newProducts.map((comp, idx) => {
                  const totalValue = (comp.uploadedCost || 0) * comp.uploadedQuantity;
                  return (
                    <div key={idx} className="p-3 rounded-lg border-2 border-green-500/20 bg-green-500/5">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                          <div className="font-medium">{comp.productCode}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                          <div className="font-medium">{comp.productName}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Kategori:</span>
                          <div className="font-medium">{comp.category || '-'}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Miktar:</span>
                          <div className="font-medium text-green-600">{comp.uploadedQuantity.toLocaleString('tr-TR')}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Maliyet:</span>
                          <div className="font-medium">{comp.uploadedCost ? `₺${comp.uploadedCost.toFixed(2)}` : '-'}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Toplam Değer:</span>
                          <div className="font-medium">₺{totalValue.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Updated Products */}
        {updatedProducts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-orange-600 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Güncellenecek Ürünler ({updatedProducts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {updatedProducts.map((comp, idx) => {
                  const changeClass = comp.quantityChange > 0 ? 'text-green-600' : comp.quantityChange < 0 ? 'text-red-600' : '';
                  const changeText = comp.quantityChange > 0 ? `+${comp.quantityChange.toLocaleString('tr-TR')}` : comp.quantityChange.toLocaleString('tr-TR');
                  const isMajorDiscrepancy = comp.quantityChange < 0 && comp.currentQuantity > 0;
                  const currentValue = (comp.currentCost || 0) * comp.currentQuantity;
                  const uploadedValue = (comp.uploadedCost || 0) * comp.uploadedQuantity;
                  const valueChange = uploadedValue - currentValue;
                  return (
                    <div key={idx} className={`p-3 rounded-lg border-2 ${
                      isMajorDiscrepancy 
                        ? 'border-red-500/40 bg-red-500/10' 
                        : 'border-orange-500/20 bg-orange-500/5'
                    }`}>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                          <div className="font-medium">{comp.productCode}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                          <div className="font-medium">{comp.productName}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Miktar:</span>
                          <div className="font-medium">{comp.currentQuantity.toLocaleString('tr-TR')}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Yeni Miktar:</span>
                          <div className="font-medium">{comp.uploadedQuantity.toLocaleString('tr-TR')}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Değişim:</span>
                          <div className={`font-bold ${changeClass}`}>
                            {changeText}
                            {isMajorDiscrepancy && (
                              <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">ÖNEMLİ</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Kategori:</span>
                          <div className="font-medium">{comp.category || '-'}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-orange-500/20 grid grid-cols-4 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Maliyet:</span>
                          <div className="font-medium">{comp.currentCost ? `₺${comp.currentCost.toFixed(2)}` : '-'}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Yeni Maliyet:</span>
                          <div className="font-medium">{comp.uploadedCost ? `₺${comp.uploadedCost.toFixed(2)}` : '-'}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Değer:</span>
                          <div className="font-medium">₺{currentValue.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Yeni Değer:</span>
                          <div className={`font-medium ${valueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ₺{uploadedValue.toFixed(2)} ({valueChange >= 0 ? '+' : ''}₺{valueChange.toFixed(2)})
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Removed Products */}
        {mode === 'clean_slate' && removedProducts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Sıfırlanacak Ürünler ({removedProducts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {removedProducts.map((comp, idx) => {
                  const currentValue = (comp.currentCost || 0) * comp.currentQuantity;
                  return (
                    <div key={idx} className="p-3 rounded-lg border-2 border-red-500/20 bg-red-500/5">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                          <div className="font-medium">{comp.productCode}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                          <div className="font-medium">{comp.productName}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Miktar:</span>
                          <div className="font-medium">{comp.currentQuantity.toLocaleString('tr-TR')}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Maliyet:</span>
                          <div className="font-medium">{comp.currentCost ? `₺${comp.currentCost.toFixed(2)}` : '-'}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Mevcut Değer:</span>
                          <div className="font-medium">₺{currentValue.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AnnualInventoryComparison;

